import BaseController from "./base_controller"
import type { Protocol } from "@/types/models"
import { localizedPath } from "@/utils/i18n"

// Connects to data-controller="protocol-info"
export default class extends BaseController {
  static targets = ["protocolSelect", "protocolInfoLink", "protocolInfoName"]
  static values = { 
    protocols: Array,
    locale: String
  }

  declare readonly protocolSelectTarget: HTMLSelectElement
  declare readonly protocolInfoLinkTarget: HTMLElement
  declare readonly protocolInfoNameTarget: HTMLElement
  declare readonly hasProtocolInfoLinkTarget: boolean
  declare readonly hasProtocolInfoNameTarget: boolean

  declare protocolsValue: Protocol[]
  declare localeValue: string

  connect(): void {
    super.connect()
    this.debug("ProtocolInfo controller connected")
    this.updateProtocolInfoLink()
  }

  // Triggered when protocol selection changes
  protocolChanged(): void {
    try {
      this.debug("Protocol changed")
      this.updateProtocolInfoLink()
    } catch (error) {
      this.handleError(error, 'protocolChanged')
    }
  }

  // Update protocol info link visibility and text
  private updateProtocolInfoLink(): void {
    try {
      if (!this.hasProtocolInfoLinkTarget) {
        this.debug("No protocol info link target found")
        return
      }

      const selectedId = this.protocolSelectTarget.value
      
      if (!selectedId) {
        this.protocolInfoLinkTarget.style.display = 'none'
        this.debug("Hiding protocol info link - no protocol selected")
        return
      }

      const protocol = this.findProtocolById(selectedId)
      if (protocol && protocol.slug) {
        // Extract protocol name from the option text (remove cycle info)
        const selectedOption = this.protocolSelectTarget.options[this.protocolSelectTarget.selectedIndex]
        const protocolName = selectedOption.text.split(' (')[0] // "Stamets Protokol (4 dny davky, 3 dny pauza)" -> "Stamets Protokol"
        
        if (this.hasProtocolInfoNameTarget) {
          this.protocolInfoNameTarget.textContent = protocolName
        }
        
        // Generate URL to public protocol page
        const protocolUrl = this.generateProtocolUrl(protocol.slug)
        const linkElement = this.protocolInfoLinkTarget.querySelector<HTMLAnchorElement>('a')
        if (linkElement) {
          linkElement.href = protocolUrl
        }
        
        this.protocolInfoLinkTarget.style.display = 'block'
        this.debug(`Showing protocol info link for: ${protocolName}, URL: ${protocolUrl}`)
      } else {
        this.protocolInfoLinkTarget.style.display = 'none'
        this.debug("Hiding protocol info link - protocol not found or missing slug")
      }
    } catch (error) {
      this.handleError(error, 'updateProtocolInfoLink')
    }
  }

  // Find protocol by ID in the protocols value
  private findProtocolById(id: string): Protocol | undefined {
    return this.protocolsValue.find(protocol => 
      protocol.id.toString() === id
    )
  }

  // Generate localized protocol URL
  private generateProtocolUrl(slug: string): string {
    const locale = this.localeValue || 'en'
    return `/${locale}/${localizedPath('protocols')}/${slug}`
  }
}

